import { useDomEvent } from 'hooks/use-dom-event'
import * as Slider from 'primitives/slider'
import * as React from 'react'
import { callAll } from 'utils/call-all'
import { tw } from 'utils/classnames'
import { createContext } from 'utils/react'

const [SliderProvider, useSlider] = createContext<Slider.SliderProps>('Slider', null!)

interface SliderRootProps extends Slider.SliderProps {
  withWheel?: boolean
}

const evtOptions = { passive: false }

export function Root({ withWheel = true, ...props }: SliderRootProps) {
  const [isHover, setIsHover] = React.useState(false)

  useDomEvent(
    useDomEvent.document,
    'wheel',
    evt => {
      if (isHover && withWheel) {
        const e = evt as WheelEvent
        e.preventDefault()
        e.stopPropagation()
        if (props.value) {
          const amount = Math.round(e.deltaY / 4) * -1
          const max = props.max ?? Infinity
          const min = props.min ?? -Infinity
          props.onValueChange?.(props.value?.map(v => Math.max(min, Math.min(max, v + amount))))
        }
      }
    },
    evtOptions,
  )

  return (
    <SliderProvider {...props}>
      <Slider.Root {...props} asChild>
        <div
          onPointerEnter={callAll(() => setIsHover(true), props.onPointerEnter)}
          onPointerLeave={callAll(() => setIsHover(false), props.onPointerLeave)}
          className={tw(
            'group relative flex cursor-pointer items-center justify-center bg-transparent',
            props.orientation === 'horizontal' ? 'h-6 w-full' : 'h-full w-6',
            props.disabled && 'cursor-not-allowed',
            props.className,
          )}
        >
          {props.children}
        </div>
      </Slider.Root>
    </SliderProvider>
  )
}

export function Track(props: Slider.SliderTrackProps) {
  const slider = useSlider()

  return (
    <Slider.Track
      {...props}
      className={tw(
        'relative block rounded-full bg-[rgba(187,_134,_252,_0.3)] shadow-md',
        slider.orientation === 'horizontal' ? 'h-2 w-full' : 'h-full w-2',
        props.className,
      )}
    />
  )
}

export function Range(props: Slider.SliderRangeProps) {
  const slider = useSlider()

  return (
    <Slider.Range
      {...props}
      className={tw(
        'absolute rounded-full bg-violet-500',
        slider.orientation === 'horizontal' ? 'h-full' : 'w-full',
        props.className,
      )}
    />
  )
}

export function Thumb(props: Slider.SliderThumbProps & { disabled?: boolean }) {
  return (
    <Slider.Thumb
      {...props}
      className={tw(
        'block size-4 cursor-pointer rounded-full bg-[#8559F9] transition-transform focus:scale-100 group-hover:scale-125',
        props.disabled && 'cursor-not-allowed',
        props.className,
      )}
    />
  )
}
